import { registerSchema } from "@eb/shared";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, ErrorBanner } from "../components/ui";
import { useAuth } from "../state/auth";
import { AuthShell, Field } from "./LoginPage";

export function RegisterPage() {
    const { signUp } = useAuth();
    const navigate = useNavigate();
    const [displayName, setDisplayName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [mailSent, setMailSent] = useState(false);

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        setError(null);

        // Validate with the same schema the API uses, so the learner sees the error
        // before a round trip rather than after it.
        const parsed = registerSchema.safeParse({ email, password, displayName });
        if (!parsed.success) {
            setError(parsed.error.issues[0]?.message ?? "请检查输入");
            return;
        }

        setBusy(true);
        try {
            const { checkInbox } = await signUp(
                parsed.data.email,
                parsed.data.password,
                parsed.data.displayName
            );
            // With email confirmation on, there is no session yet -- tell the learner to
            // go and click the link rather than dropping them on a login screen. This is
            // also where an address that was already registered lands; see state/auth.tsx.
            if (checkInbox) setMailSent(true);
            else navigate("/", { replace: true });
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "注册失败，请稍后重试");
        } finally {
            setBusy(false);
        }
    }

    // Shown for a brand-new address and for one that was already registered, word for word.
    // The difference between the two cases lives in the inbox, not on this screen -- saying
    // "this address is taken" here would tell anyone which addresses have accounts.
    if (mailSent) {
        return (
            <AuthShell title="就差一步" subtitle="去邮箱收一封信">
                <div className="space-y-4 text-sm text-ink-soft">
                    <p>
                        我们向 <strong className="text-ink">{email}</strong> 发了一封邮件，
                        点开里面的链接就能继续。
                    </p>
                    <p>
                        如果这个邮箱之前已经注册过，收到的会是一封设置新密码的邮件 ——
                        用它就能重新进入原来的账号。
                    </p>
                    <p className="text-xs">没看到的话，翻一下垃圾邮件。</p>
                    <Link to="/login" className="block text-center font-medium text-brand hover:underline">
                        去登录
                    </Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell title="注册" subtitle="310 道题，从今天开始排进复习计划">
            <form onSubmit={handleSubmit} className="space-y-4">
                {error ? <ErrorBanner message={error} /> : null}

                <Field
                    label="昵称"
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    autoComplete="nickname"
                    required
                    placeholder="怎么称呼你"
                />
                <Field
                    label="邮箱"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                />
                <Field
                    label="密码"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="new-password"
                    required
                    placeholder="至少 8 个字符"
                />

                <Button type="submit" loading={busy} className="w-full">
                    创建账号
                </Button>

                <p className="text-center text-sm text-ink-soft">
                    已经有账号了？{" "}
                    <Link to="/login" className="font-medium text-brand hover:underline">
                        去登录
                    </Link>
                </p>
            </form>
        </AuthShell>
    );
}
